export { PdfGen, PdfImage, PdfFont, PdfBrush, PdfRect } from './pdf/pdf';
export type { IFileCache } from './pdf/pdf';
export { setFileCache, getFileCache, getFontLoader } from './pdf/pdf';

// Image pipeline
export { RawImage } from './pdf/rawImage';
export type { IImageCanvas, IImageCanvasLoader } from './pdf/imageCanvas';
export { setImageCanvasLoader, getImageCanvasLoader } from './pdf/imageCanvas';
export { decodePng, encodePng, isPng } from './pdf/imageCodec';
export { NodeImageCanvas, NodeImageCanvasLoader } from './pdf/imageCanvasNode';

// Base layout
export { ILayoutBaseSymbol } from './layoutBase/layoutBase';
export type { ILayoutBaseGen } from './layoutBase/layoutBase';
export { registerLayoutBase } from './layoutBase/layoutBaseRegistrator';
export type { OptsBase } from './layoutBase/optsBase';

// BaseTest layout
export { ILayoutBaseTestSymbol } from './layoutBaseTest/layoutBaseTest';
export type { ILayoutBaseTestGen } from './layoutBaseTest/layoutBaseTest';
export { registerLayoutBaseTest } from './layoutBaseTest/layoutBaseTestRegistrator';
export type { OptsBaseTest } from './layoutBaseTest/optsBaseTest';

// Label layout
export { ILayoutLabelSymbol as ILayoutLabelGenSymbol } from './layoutLabel/layoutLabel';
export type { ILayoutLabelGen } from './layoutLabel/layoutLabel';
export { registerLayoutLabel } from './layoutLabel/layoutLabelRegistrator';
export type { OptsLabel } from './layoutLabel/optsLabel';

// Multi layout
export { ILayoutMultiSymbol as ILayoutMultiGenSymbol } from './layoutMulti/layoutMulti';
export type { ILayoutMultiGen } from './layoutMulti/layoutMulti';
export { registerLayoutMulti } from './layoutMulti/layoutMultiRegistrator';
export type { OptsMulti } from './layoutMulti/optsMulti';

// Order layout
export { ILayoutListSymbol } from './layoutList/layoutList';
export type { ILayoutListGen } from './layoutList/layoutList';
export { registerLayoutList } from './layoutList/layoutListRegistrator';
export type { OptsList } from './layoutList/optsList';

// Image layout
export { ILayoutImageSymbol as ILayoutImageGenSymbol } from './layoutImage/layoutImage';
export type { ILayoutImageGen } from './layoutImage/layoutImage';
export { registerLayoutImage } from './layoutImage/layoutImageRegistrator';
export type { OptsImage, ImageTuple } from './layoutImage/optsImage';

// LabelText layout
export { ILayoutLabelTextSymbol as ILayoutLabelTextGenSymbol } from './layoutLabelText/layoutLabelText';
export type { ILayoutLabelTextGen } from './layoutLabelText/layoutLabelText';
export { registerLayoutLabelText } from './layoutLabelText/layoutLabelTextRegistrator';
export type { OptsLabelText, LabelTextTuple } from './layoutLabelText/optsLabelText';

// Single layout
export { ILayoutSingleSymbol } from './layoutSingle/layoutSingle';
export type { ILayoutSingleGen } from './layoutSingle/layoutSingle';
export { registerLayoutSingle } from './layoutSingle/layoutSingleRegistrator';
export type { OptsSingle } from './layoutSingle/optsSingle';

// ImageSingle layout (Image + equation generator)
export { ILayoutImageSingleSymbol } from './layoutImageSingle/layoutImageSingle';
export type { ILayoutImageSingleGen } from './layoutImageSingle/layoutImageSingle';
export { registerLayoutImageSingle } from './layoutImageSingle/layoutImageSingleRegistrator';
export type { OptsImageSingle } from './layoutImageSingle/optsImageSingle';

// List generators
export { registerGenList } from './genList/genListRegistrator';
export { SimpleGenList } from './genList/simpleGenList';
export { NumberArrayGen } from './genList/numberArrayGen';

// Helpers
export { genMulti, genSingle, calcOrigin } from './core/genHelper';
export { calcIdealGrid, calcGridRects } from './pdf/board/grid';
export type { GridRectsResult } from './pdf/board/grid';
export { placeObjects } from './core/placeObjects';
export type { PlacedObject } from './core/placeObjects';

// Label types
export { GLabel, GLabelType } from './pdf/glabel';
export type { GTuple, LabelData } from './pdf/glabel';

// Preview lines for thumbnails
export { EPreviewLineType, PreviewLines } from './pdf/preview/previewLines';
export type { PreviewLine } from './pdf/preview/previewLines';

// Exercise system
export { generateExe } from './exe/exeRenderer';
export { registerExe } from './exe/exeRegistrator';
export { IExerciseSymbol } from './exe/exercise';
export type { IExercise, ExerciseData } from './exe/exercise';
export { IColorSchemaSymbol } from './exe/colorSchema';
export type { IColorSchema } from './exe/colorSchema';
export {
  EExeItemType,
  ExeResults,
  createDefaultExeOptions,
  createDefaultExeItem,
  createNewPageItem,
} from './exe/exeProject';
export type { ExeProject, ExeItem, ExeOptions } from './exe/exeProject';

// GenPart (drawing part generators)
export { IGenPartSymbol } from './genPart/genPart';
export type { IGenPart } from './genPart/genPart';
export { registerGenPart } from './genPart/genPartRegistrator';

// SVG rendering
export { parseXml } from './pdf/svg/svg';
export type { SvgNode, SvgDrawOptions } from './pdf/svg/svg';

// Re-export symbols from base for provider access
export {
  IGenEqSymbol,
  IGenMultiSymbol,
  IGenSingleSymbol,
  IGenListSymbol,
  IGenChainSymbol,
} from '@educraft/base';
export type { IArchiveUrls } from '@educraft/base';

// ZIP utilities
export { decompressZip } from './pdf/zip';

// ArchiveGenMulti generator
export { ArchiveGenMulti, configureArchiveGenMulti } from './genMulti/generators/archiveGenMulti';

// GenMulti generators
export { registerGenMulti } from './genMulti/genMultiRegistrator';
export { registerGenUnit } from './genUnit/genUnitRegistrator';

// Book rendering
export { render } from './pdf/book';
export type { BookOptions } from './pdf/book';

// Content generators (IGenEq)
export { registerGenEq } from './genEq/genEqRegistrator';
export * from './genEq/generators/mathAddition';
export * from './genEq/generators/mathAdditionSubtraction';
export * from './genEq/genHelper';

export { AnyQr, registerAnyQr, IAnyQrSymbol } from './pdf/qr/AnyQr';
export type { IAnyQrGen, OptsQr, QrProj, QrCard, QrCommon, QrCont, AnyQrImage } from './pdf/qr/AnyQr';

export * from './genSingle/genSingleRegistrator';
export * from './genSingle/generators/mathAdvanced';
export * from './genSingle/generators/mathClocks';
export * from './genSingle/generators/mathFractions';

export * from './genEq/generators/mathSubtraction';
export * from './genEq/generators/mathMultiplication';
export * from './genEq/generators/mathDivision';
export * from './genEq/generators/mathEquations';
export * from './genEq/generators/mathSpecialized';
export * from './genEq/generators/mathWordForms';
export * from './genEq/generators/mathOthers';
export * from './genEq/generators/mathIvt';
export * from './genEq/generators/mathWordProblems';
